import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useState } from "react";
import ProjectModal from "./ProjectModal";
import type { Project } from "@/types/project";
import { Construction } from "lucide-react";
import falkorsInterior1 from "@/assets/projects/falkors-interior-1.jpeg";
import falkorsInterior2 from "@/assets/projects/falkors-interior-2.jpeg";
import falkorsInterior3 from "@/assets/projects/falkors-interior-3.jpeg";
import mastiTower1 from "@/assets/projects/5masti-tower-1.jpg";
import mastiTower2 from "@/assets/projects/5masti-tower-2.jpg";
import mastiTower3 from "@/assets/projects/5masti-tower-3.jpg";
import mastiTower4 from "@/assets/projects/5masti-tower-4.jpg";
import mastiTowerCover from "@/assets/projects/5masti-tower-cover.jpg";
import boulderstationInterior1 from "@/assets/projects/boulderstation-4.jpg";
import boulderstationInterior2 from "@/assets/projects/boulderstation-5.webp";
import boulderstationInterior3 from "@/assets/projects/boulderstation-6.jpg";
import ogreTower1 from "@/assets/projects/ogre-tower-1.jpg";
import ogreTower2 from "@/assets/projects/ogre-tower-2.jpg";
import ogreTower3 from "@/assets/projects/ogre-tower-3.jpg";
import ogreTower4 from "@/assets/projects/ogre-tower-4.jpg";
import ogreTowerCover from "@/assets/projects/ogre-tower-cover.jpg";
import ronimiministeeriumInterior1 from "@/assets/projects/ronimiministeerium-1.jpg";
import ronimiministeeriumInterior2 from "@/assets/projects/ronimiministeerium-2.jpg";
import ronimiministeeriumInterior3 from "@/assets/projects/ronimiministeerium-3.jpg";
import ronimiministeeriumInterior4 from "@/assets/projects/ronimiministeerium-4.jpg";
import netsportInterior1 from "@/assets/projects/netsport-1.jpg";
import netsportInterior2 from "@/assets/projects/netsport-2.jpg";
import netsportInterior3 from "@/assets/projects/netsport-3.jpg";
import netsportInterior4 from "@/assets/projects/netsport-4.jpg";
import netsportInterior5 from "@/assets/projects/netsport-5.jpg";
import mustikaInterior1 from "@/assets/projects/mustika-1.jpg";
import ronimaa1 from "@/assets/projects/ronimaa-1.jpg";
import ronimaa2 from "@/assets/projects/ronimaa-2.jpg";
import ronimaa3 from "@/assets/projects/ronimaa-3.jpg";
import ronimaaCover from "@/assets/projects/ronimaa-cover.jpg";
import tallinnKindergartenCover from "@/assets/projects/tallinn-kindergarten-cover.jpg";
import tallinnKindergarten1 from "@/assets/projects/tallinn-kindergarten-1.jpg";
import tallinnKindergarten2 from "@/assets/projects/tallinn-kindergarten-2.jpg";
import tallinnKindergarten3 from "@/assets/projects/tallinn-kindergarten-3.jpg";
import tallinnKindergarten4 from "@/assets/projects/tallinn-kindergarten-4.jpg";
import scalaDream1 from "@/assets/projects/scala-dream-4.jpg";
import scalaDream2 from "@/assets/projects/scala-dream-5.jpg";
import verticalVilniusCover from "@/assets/projects/vertical-vilnius-cover.png";
import verticalVilnius1 from "@/assets/projects/vertical-vilnius-1.jpg";
import verticalVilnius2 from "@/assets/projects/vertical-vilnius-2.jpg";
import verticalVilnius3 from "@/assets/projects/vertical-vilnius-3.jpg";
import koalaTallinnCover from "@/assets/projects/koala-tallinn-cover.jpeg";
import koalaTallinn1 from "@/assets/projects/koala-tallinn-1.jpeg";
import koalaTallinn2 from "@/assets/projects/koala-tallinn-2.jpeg";
import koalaTallinn3 from "@/assets/projects/koala-tallinn-3.jpeg";
import koalaTallinn4 from "@/assets/projects/koala-tallinn-4.jpeg";
import tehase22TartuCover from "@/assets/projects/tehase22-tartu-cover.jpg";
import tehase22Tartu1 from "@/assets/projects/tehase22-tartu-1.jpg";
import tehase22Tartu2 from "@/assets/projects/tehase22-tartu-2.jpg";

// Helper function to get badge color based on project type
const getTypeBadgeClass = (type: Project["type"]) => {
  switch (type) {
    case "Indoor Bouldering":
      return "bg-primary text-white hover:bg-primary/90";
    case "Outdoor Tower":
      return "bg-teal-600 text-white hover:bg-teal-700";
    case "Educational Facility":
      return "bg-sage text-white hover:bg-sage/90";
    case "Training Facility":
      return "bg-slate-600 text-white hover:bg-slate-700";
    case "Rope Climbing":
      return "bg-amber-600 text-white hover:bg-amber-700";
    default:
      return "bg-primary text-white hover:bg-primary/90";
  }
};

const projects: Project[] = [
  // 2026 - Future Project
  {
    name: "Falkors Bouldering Center - New Facility",
    location: "Riga, Latvia",
    area: "4500 m²",
    year: "2026",
    type: "Indoor Bouldering" as const,
    highlight: "Coming September 2026",
    description: "We're building the largest bouldering facility in the Baltic region - twice the size of our current center. This ambitious project will feature 4500 m² of floor space with approximately 3000 m² of climbing surfaces, setting a new standard for climbing facilities in the region. Drawing on our experience operating the current largest center, we're creating an even more comprehensive destination for climbers of all levels.",
    ourWork: [
      "Large-scale Facility Design",
      "3000 m² Climbing Surface",
      "Advanced Training Areas",
      "Competition-grade Walls",
      "Complete Amenities Integration",
      "Community Spaces",
      "Turnkey Development"
    ],
    metrics: [
      { label: "Total Floor Area", value: "4500 m²" },
      { label: "Climbing Surface", value: "~3000 m²" },
      { label: "Size Comparison", value: "2× current center" },
      { label: "Planned Opening", value: "September 2026" },
      { label: "Status", value: "In Development" },
      { label: "Scale", value: "Largest in Baltics" }
    ],
    images: [] // Future project - no images yet
  },
  
  // 2025 Projects
  {
    name: "Mustika Ronimisministeerium",
    location: "Mustika, Estonia",
    area: "1100 m²",
    year: "2025",
    type: "Indoor Bouldering",
    highlight: "Latest Project",
    website: "https://ronimisministeerium.ee/tallinn/",
    description: "Our newest Estonian project delivers a comprehensive climbing facility with 1100 m² of floor space and 700 m² of climbing surfaces. We designed and built specialized zones for children and beginners, integrated a full training area, and equipped the facility with complete amenities including changing rooms, showers, and a unisex sauna. This modern facility combines functional design with community-focused features, offering equipment rental and retail services.",
    ourWork: [
      "Facility Planning & Design",
      "700 m² Wall Construction",
      "Children's & Beginners' Zones",
      "Training Area Integration",
      "Amenities Development",
      "Equipment & Retail Setup",
      "Complete Turnkey Solution"
    ],
    metrics: [
      { label: "Total Floor Area", value: "1100 m²" },
      { label: "Climbing Surface", value: "700 m²" },
      { label: "Special Zones", value: "Kids & beginners" },
      { label: "Training Area", value: "Integrated" },
      { label: "Sauna", value: "Unisex (October)" },
      { label: "Status", value: "Newly Completed" }
    ],
    images: [mustikaInterior1]
  },
  {
    name: "Raadi Tartu",
    location: "Tartu, Estonia",
    area: "Custom Installation",
    year: "2025",
    type: "Educational Facility",
    description: "We designed and constructed a specialized climbing wall installation for a kindergarten in Tartu, creating a safe and engaging environment for young children. This project showcases our expertise in educational facility design, integrating climbing structures within beautiful wooden architecture. The installation prioritizes safety while encouraging physical development and confidence building in young climbers.",
    ourWork: [
      "Child-Safe Wall Design",
      "Custom Wall Construction",
      "Safety Surface Integration",
      "Age-Appropriate Route Setting",
      "Educational Space Integration",
      "Quality Installation"
    ],
    metrics: [
      { label: "Project Type", value: "Educational" },
      { label: "Target Age", value: "Kindergarten" },
      { label: "Safety Standards", value: "Child-certified" },
      { label: "Installation", value: "Custom built" },
      { label: "Completion", value: "2025" }
    ],
    images: [tallinnKindergartenCover, tallinnKindergarten1, tallinnKindergarten2, tallinnKindergarten3, tallinnKindergarten4]
  },
  
  // 2024 Projects
  {
    name: "Tehase 22 Climbing Factory",
    location: "Tartu, Estonia",
    area: "165 m²",
    year: "2024",
    type: "Rope Climbing",
    website: "https://ronimistehas.com/koiesaal/",
    description: "We manufactured precision climbing walls for this impressive rope climbing facility featuring 500 m² of wall space with weekly route renewals. This manufacturing-focused project showcases our CNC production capabilities and technical expertise in creating high-quality climbing surfaces. The facility includes automatic belay devices, comprehensive amenities with a café, equipment shop, training rooms for yoga and acrobatics, weight training areas, and specialized children's climbing walls. Our precision-manufactured walls enable a complete climbing experience from beginners to advanced climbers.",
    ourWork: [
      "CNC Wall Manufacturing",
      "500 m² Surface Production",
      "Precision Engineering",
      "Multi-surface Fabrication",
      "Quality Control",
      "Manufacturing Logistics"
    ],
    metrics: [
      { label: "Floor Area", value: "165 m²" },
      { label: "Climbing Surface", value: "500 m²" },
      { label: "Route Updates", value: "Weekly renewals" },
      { label: "Auto-belay Devices", value: "Available" },
      { label: "Our Role", value: "Manufacturing only" },
      { label: "Special Features", value: "Café, shop, training rooms" }
    ],
    images: [tehase22TartuCover, tehase22Tartu1, tehase22Tartu2]
  },
  {
    name: "Netsport Sport Center",
    location: "Parnu, Estonia",
    area: "700 m²",
    year: "2024",
    type: "Indoor Bouldering",
    website: "https://netspordihall.ee/spordialad/ronimine/",
    description: "We engineered a rare multi-height climbing facility featuring the Baltics' most impressive 15-meter main wall. This innovative project combines various climbing disciplines - high wall climbing, bouldering, and rope climbing - with advanced safety systems including 10 self-belay devices. Our design integrates spectacular views and creates a comprehensive climbing experience within a modern sports complex.",
    ourWork: [
      "15m High Wall Engineering",
      "Multi-discipline Design",
      "4m Boulder Wall Construction",
      "10m Rope Wall Installation",
      "Self-belay System Integration",
      "Complete Safety Infrastructure"
    ],
    metrics: [
      { label: "Total Area", value: "700 m²" },
      { label: "Main Wall Height", value: "15m (tallest in Baltics)" },
      { label: "Boulder Wall", value: "4m height" },
      { label: "Rope Wall", value: "10m height" },
      { label: "Self-belay Devices", value: "10 units" },
      { label: "Special Feature", value: "Beach volleyball views" }
    ],
    images: [netsportInterior4, netsportInterior1, netsportInterior2, netsportInterior3, netsportInterior5]
  },
  {
    name: "Ronimaa - Parnu Boulder Gym",
    location: "Parnu, Estonia",
    area: "150+ m²",
    year: "2024",
    type: "Indoor Bouldering",
    website: "https://www.ronimaa.ee/",
    description: "We designed and built a bouldering wall with great community focus, featuring nearly 30 challenging routes with regular updates. This facility offers exceptional accessibility with 24/7 access for season ticket holders and extended hours for day visitors. The compact design includes a training and recreation area on the second floor with optimal views of the climbing area, creating a social hub for the climbing community. Complete with rental equipment, changing facilities, and retail space.",
    ourWork: [
      "Community-focused Design",
      "150+ m² Wall Construction",
      "30+ Route Capacity",
      "24/7 Access System",
      "Two-floor Layout",
      "Training Area Integration",
      "Facilities & Amenities"
    ],
    metrics: [
      { label: "Climbing Surface", value: "150+ m²" },
      { label: "Active Routes", value: "30+ problems" },
      { label: "Annual Variety", value: "300+ challenges/year" },
      { label: "Update Frequency", value: "Every 2 weeks" },
      { label: "Access", value: "24/7 for members" },
      { label: "Special Feature", value: "2nd floor training area" }
    ],
    images: [ronimaaCover, ronimaa1, ronimaa2, ronimaa3]
  },
  
  // 2023 Projects
  {
    name: "5 Masti Outdoor Tower",
    location: "Ozolnieki, Latvia",
    area: "215 m²",
    year: "2023",
    type: "Outdoor Tower",
    website: "https://5masti.lv/klinsu-kapsana/",
    description: "We designed and constructed one of the region's most impressive climbing structures, featuring an 18-meter tower with integrated climbing wall, bouldering area, and free-fall platform. This multi-functional outdoor facility showcases our expertise in complex structural engineering and diverse climbing experience integration.",
    ourWork: [
      "Multi-function Tower Design",
      "Structural Engineering",
      "Weather-resistant Construction",
      "Climbing Wall Installation",
      "Boulder Section Integration",
      "Free-fall Platform Setup",
      "Viewing Deck Construction"
    ],
    metrics: [
      { label: "Tower Height", value: "18m" },
      { label: "Climbing Wall", value: "15m × 7.5m" },
      { label: "Boulder Area", value: "50 m²" },
      { label: "Free-fall Height", value: "17m" },
      { label: "Total Site", value: "215 m²" },
      { label: "Special Feature", value: "Lake view platform" }
    ],
    images: [mastiTowerCover, mastiTower1, mastiTower2, mastiTower3, mastiTower4]
  },
  {
    name: "Boulderstation Enschede",
    location: "Enschede, Netherlands",
    area: "1500 m²",
    year: "2023",
    type: "Indoor Bouldering",
    website: "https://www.boulderstation enschede.nl/en_GB",
    description: "We designed and constructed a spectacular two-floor climbing facility that redefines the bouldering experience. This expansive project features precision-engineered climbing walls spanning multiple zones, complemented by competition-grade surfaces and comprehensive training facilities. Our team delivered a complete solution from concept to installation, creating a space where climbers of all levels can push their limits.",
    ourWork: [
      "Architectural Design",
      "Structural Engineering",
      "CNC Manufacturing",
      "Multi-level Construction",
      "Competition Wall Installation",
      "Training Equipment Setup"
    ],
    metrics: [
      { label: "Total Area", value: "1500 m²" },
      { label: "Climbing Surface", value: "1000 m²" },
      { label: "Floors", value: "2 levels" },
      { label: "Competition Walls", value: "2 professional grade" },
      { label: "Training Area", value: "300 m²" },
      { label: "Specialized Stations", value: "7 zones" }
    ],
    images: [boulderstationInterior1, boulderstationInterior2, boulderstationInterior3]
  },
  {
    name: "Ronimiministeerium T1",
    location: "Tallinn, Estonia",
    area: "800 m²",
    year: "2023",
    type: "Indoor Bouldering",
    highlight: "Largest in Estonia",
    website: "https://ronimisministeerium.ee/tallinn/en/t1/",
    description: "We engineered and built Estonia's largest bouldering facility, delivering an expansive climbing experience in the heart of Tallinn. This comprehensive project showcases our ability to maximize climbing variety and capacity, featuring over 150 diverse routes for adults, dedicated children's areas, and complete amenity integration including training facilities and wellness spaces.",
    ourWork: [
      "Large-scale Facility Design",
      "800 m² Wall Construction",
      "Route Setting Infrastructure",
      "Children's Climbing Zones",
      "Training Area Integration",
      "Complete Facility Fit-out"
    ],
    metrics: [
      { label: "Floor Area", value: "800 m²" },
      { label: "Climbing Surface", value: "800 m²" },
      { label: "Adult Routes", value: "150+ problems" },
      { label: "Children's Walls", value: "2 dedicated areas" },
      { label: "Wall Height", value: "Up to 4.5m" },
      { label: "Special Feature", value: "Sauna & wellness" }
    ],
    images: [ronimiministeeriumInterior1, ronimiministeeriumInterior2, ronimiministeeriumInterior3, ronimiministeeriumInterior4]
  },
  
  // 2022 Projects
  {
    name: "Falkors Bouldering Center",
    location: "Riga, Latvia",
    area: "1400 m²",
    year: "2022",
    type: "Indoor Bouldering",
    highlight: "Biggest in Baltics",
    website: "https://www.boulderings.lv/",
    description: "We delivered the largest bouldering facility in the Baltic region, creating a comprehensive climbing destination that serves both beginners and experienced athletes. Our team engineered and constructed modernly equipped walls with diverse route configurations, integrated training zones, and complete amenity spaces to ensure an exceptional climbing experience.",
    ourWork: [
      "Design & Engineering",
      "CNC Manufacturing",
      "Wall Construction",
      "Route Setting Infrastructure",
      "On-site Assembly",
      "Complete Installation"
    ],
    metrics: [
      { label: "Total Area", value: "1400 m²" },
      { label: "Climbing Surface", value: "1100 m²" },
      { label: "Wall Height", value: "Up to 4.5m" },
      { label: "Route Capacity", value: "150+ problems" },
      { label: "Training Zones", value: "3 specialized areas" }
    ],
    images: [falkorsInterior1, falkorsInterior2, falkorsInterior3]
  },
  {
    name: "Koala Climbing",
    location: "Tallinn, Estonia",
    area: "180 m²",
    year: "2022",
    type: "Indoor Bouldering",
    description: "We designed and constructed a distinctive bouldering facility in Tallinn featuring bold geometric patterns with natural wood, white, and black surfaces. This unique project showcases our expertise in creating visually striking climbing spaces with flowing, sculptural wall designs. The facility combines artistic architectural elements with functional climbing surfaces, creating an immersive environment that challenges climbers while providing an aesthetically memorable experience.",
    ourWork: [
      "Architectural Design",
      "Geometric Wall Construction",
      "Multi-surface Integration",
      "Sculptural Elements",
      "Safety System Installation",
      "Complete Facility Setup"
    ],
    metrics: [
      { label: "Climbing Surface", value: "180 m²" },
      { label: "Design Style", value: "Geometric patterns" },
      { label: "Wall Features", value: "Sculptural forms" },
      { label: "Surface Types", value: "Wood, black, white" },
      { label: "Completion", value: "2022" }
    ],
    images: [koalaTallinnCover, koalaTallinn1, koalaTallinn2, koalaTallinn3, koalaTallinn4]
  },
  {
    name: "Ogre Outdoor Tower",
    location: "Ogre, Latvia",
    area: "180 m²",
    year: "2022",
    type: "Outdoor Tower",
    description: "We designed and constructed a versatile outdoor climbing tower in Ogre, delivering a robust structure that withstands all weather conditions. This impressive facility features multiple climbing surfaces and provides an excellent outdoor climbing experience for the local community and visitors. Our engineering expertise ensured a safe, durable installation that enhances the region's outdoor recreation offerings.",
    ourWork: [
      "Outdoor Tower Design",
      "Structural Engineering",
      "Weather-resistant Construction",
      "Multi-surface Climbing Wall",
      "Safety System Integration",
      "Complete Installation"
    ],
    metrics: [
      { label: "Tower Height", value: "24m" },
      { label: "Climbing Surface", value: "1050 m²" },
      { label: "Wall Types", value: "Multiple faces" },
      { label: "Construction", value: "All-weather" },
      { label: "Completion", value: "2022" }
    ],
    images: [ogreTowerCover, ogreTower1, ogreTower2, ogreTower3, ogreTower4]
  },
  
  // 2021 Projects
  {
    name: "4Vertical Climbing Gym",
    location: "Vilnius, Lithuania",
    area: "225 m²",
    year: "2021",
    type: "Indoor Bouldering",
    website: "https://www.vertical.lt/",
    description: "We designed and constructed a modern bouldering facility in Vilnius, featuring diverse climbing surfaces with geometric patterns and a comprehensive training area. This project showcases our expertise in creating versatile climbing spaces with varied wall angles and textures, optimized for climbers of all skill levels. The facility combines bold color schemes with functional design, delivering an engaging climbing experience in a bright, spacious environment.",
    ourWork: [
      "Facility Design & Planning",
      "Multi-angle Wall Construction",
      "Geometric Surface Design",
      "Training Area Integration",
      "Complete Installation",
      "Safety System Setup"
    ],
    metrics: [
      { label: "Climbing Surface", value: "225 m²" },
      { label: "Wall Configurations", value: "Multiple angles" },
      { label: "Design Style", value: "Modern geometric" },
      { label: "Training Features", value: "Integrated" },
      { label: "Completion", value: "2021" }
    ],
    images: [verticalVilniusCover, verticalVilnius1, verticalVilnius2, verticalVilnius3]
  },
  
  // 2020 Projects
  {
    name: "Scala Dream",
    location: "Klaipeda, Lithuania",
    area: "145 m²",
    year: "2020",
    type: "Rope Climbing",
    website: "https://www.scaladream.com/",
    description: "We designed and constructed specialized rope climbing walls for this entertainment and training facility in Klaipeda, featuring a striking 10-meter lead wall with vibrant geometric patterns. This project showcases our expertise in creating visually captivating rope climbing installations that combine safety, functionality, and bold aesthetic design. The facility integrates our custom-manufactured climbing walls with obstacle course elements, creating a comprehensive activity center for climbers and athletes of all levels.",
    ourWork: [
      "10m Rope Wall Design",
      "Geometric Pattern Engineering",
      "Lead Climbing Wall Construction",
      "Auto-belay Integration",
      "Custom Visual Design",
      "Safety System Installation"
    ],
    metrics: [
      { label: "Wall Height", value: "10m lead wall" },
      { label: "Climbing Surface", value: "145 m²" },
      { label: "Wall Type", value: "Rope climbing" },
      { label: "Special Features", value: "Auto-belay systems" },
      { label: "Design Style", value: "Geometric patterns" },
      { label: "Completion", value: "2020" }
    ],
    images: [scalaDream1, scalaDream2]
  }
];

const Projects = () => {
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const handleProjectClick = (project: Project) => {
    console.log(`Opening project: ${project.name} with ${project.images.length} images`);
    setSelectedProject(project);
    setModalOpen(true);
  };

  return (
    <section id="projects" className="py-16 bg-gradient-hero text-white relative overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute top-20 right-10 w-72 h-72 bg-primary/10 rounded-full blur-3xl" />
      <div className="absolute bottom-20 left-10 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
      <div className="absolute inset-0 dot-pattern opacity-5" />
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-3xl mb-10 text-center mx-auto">
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-display text-white mb-4">
            COMPLETED CLIMBING GYM PROJECTS
          </h2>
          <p className="text-lg text-white/80">
            Professional climbing gym construction portfolio - 10+ completed bouldering gym builds, 
            commercial climbing wall installations, and indoor climbing facilities across Europe.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {projects.map((project, index) => (
            <Card 
              key={index}
              onClick={() => handleProjectClick(project)}
              className="overflow-hidden hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 bg-background/95 backdrop-blur-sm border-primary/20 group cursor-pointer"
            >
              {/* Cover Image */}
              {project.images && project.images.length > 0 ? (
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={project.images[0]}
                    alt={`${project.name} - Professional ${project.type.toLowerCase()} construction and installation project featuring ${project.area} climbing facility in ${project.location}`}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    loading="lazy"
                    width={404}
                    height={192}
                  />
                </div>
              ) : (
                <div className="relative h-48 overflow-hidden bg-gradient-hero">
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
                    <Construction className="text-white w-12 h-12" strokeWidth={1.5} />
                    <span className="text-white text-2xl font-display">UNDER CONSTRUCTION</span>
                  </div>
                </div>
              )}
              
              <div className="p-6 relative">
                {/* Decorative Corner */}
                <div className="absolute top-0 right-0 w-20 h-20 bg-primary/10 transform rotate-45 translate-x-10 -translate-y-10 group-hover:translate-x-8 group-hover:-translate-y-8 transition-transform" />
                
                <div className="relative z-10">
                  <div className="flex gap-2 mb-4 flex-wrap">
                    <Badge className={`${getTypeBadgeClass(project.type)} shadow-lg`}>
                      {project.type}
                    </Badge>
                    {project.highlight && (
                      <Badge className="bg-purple-600 text-white hover:bg-purple-700 shadow-sm text-xs px-2 py-0.5">
                        {project.highlight}
                      </Badge>
                    )}
                  </div>
                  
                  <h3 className="text-2xl font-display mb-2 text-foreground">
                    {project.name}
                  </h3>
                  
                  <p className="text-muted-foreground mb-4">
                    {project.location}
                  </p>
                  
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-primary font-semibold">
                      {project.area}
                    </span>
                    <span className="text-muted-foreground">
                      {project.year}
                    </span>
                  </div>
                  
                  <div className="mt-4 text-xs text-primary font-semibold uppercase tracking-wider">
                    Click to view details →
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
      
      <ProjectModal
        project={selectedProject}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
      />
    </section>
  );
};

export default Projects;